import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import API, { acceptReservation, declineReservation } from '../utils/api';
import BusinessDetailModal from '../components/BusinessDetailModal';
import BookingModal from '../components/BookingModal';
import BusinessCalendar from '../components/BusinessCalendar';
import BusinessCalendarPage from '../pages/BusinessCalendarPage';
import ReservationCard from '../components/ReservationCard';
import UserProfile from '../components/UserProfile';
import BookingDetailsModal from '../components/BookingDetailsModal';
import SimpleBookingDetailsModal from '../components/SimpleBookingDetailsModal';
import RescheduleBookingModal from '../components/RescheduleBookingModal';
import CancelBookingModal from '../components/CancelBookingModal';
import { ThemeToggle } from '../contexts/ThemeContext';
import AnimatedLayout from '../components/AnimatedLayout';
import ModernSidebar from '../components/Sidebar';
import LeafletMapPicker from '../components/LeafletMapPicker';
import BusinessMapModal from '../components/BusinessMapModal';
import WeeklyAvailability from '../components/WeeklyAvailability';
import ErrorBoundary from '../components/ErrorBoundary';
import ConversationList from '../components/ConversationList';
import ChatWindow from '../components/ChatWindow';
import useMessaging from '../hooks/useMessaging';
import { motion, AnimatePresence } from 'framer-motion';
import { authAPI } from '../api/auth';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';

function Home() {
  const [user, setUser] = useState(null);
  const [userBusiness, setUserBusiness] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [userBookings, setUserBookings] = useState([]);
  const [businessReservations, setBusinessReservations] = useState([]);
  const [businessRating, setBusinessRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('discover'); // 'discover', 'bookings', 'profile', 'business'
  const [businessView, setBusinessView] = useState('overview'); // 'overview', 'calendar', 'reservations'
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showBusinessDetailModal, setShowBusinessDetailModal] = useState(false);
  const [showBusinessEditModal, setShowBusinessEditModal] = useState(false);
  
  // Booking management modals
  const [showBookingDetailsModal, setShowBookingDetailsModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  
  // Map modal state
  const [showMapModal, setShowMapModal] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    type: '',
    location: '',
    rating: '',
    priceRange: ''
  });

  const navigate = useNavigate();
  const location = useLocation();
  const businessesPerPage = 12;

  // Messaging hook
  const {
    conversations,
    activeConversation,
    messages,
    loading: messagesLoading,
    fetchConversations,
    fetchMessages,
    createConversation,
    sendMessage,
    markAsRead,
    deleteConversation,
    setActiveConversation,
  } = useMessaging();

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    // Check if current path is /profile and set active section accordingly
    if (location.pathname === '/profile') {
      setActiveSection('profile');
    }
  }, [location]);

  useEffect(() => {
    applyFilters();
  }, [businesses, searchTerm, filters]);

  // Fetch conversations when user is loaded
  useEffect(() => {
    if (user) {
      fetchConversations();
    }
  }, [user, fetchConversations]);

  // Fetch messages when active conversation changes
  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation.id);
      markAsRead(activeConversation.id);
    }
  }, [activeConversation?.id]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch user data
      const userRes = await API.get('/auth/me');
      setUser(userRes.data);

      // Fetch user's business if they own one
      try {
        const businessRes = await API.get('/api/business/user-business');
        if (businessRes.data.business) {
          setUserBusiness(businessRes.data.business);
          
          // Fetch business rating
          try {
            const ratingRes = await API.get(`/api/reviews/business/${businessRes.data.business.id}`);
            if (ratingRes.data?.stats) {
              setBusinessRating(ratingRes.data.stats.averageRating);
            }
          } catch (ratingErr) {
            console.log('No reviews yet for this business');
            setBusinessRating(0);
          }
          
          // If user owns a business, also fetch their reservations
          const reservationsRes = await API.get('/api/business/reservations');
          // Ensure businessReservations is always an array
          const reservations = reservationsRes.data?.reservations || reservationsRes.data || [];
          setBusinessReservations(Array.isArray(reservations) ? reservations : []);
        }
      } catch (businessErr) {
        // User doesn't own a business
        console.log('User does not own a business');
      }

      // Fetch all businesses
      const businessRes = await API.get('/api/businesses/discover');
      setBusinesses(businessRes.data);

      // Fetch user bookings if authenticated
      try {
        const bookingsRes = await API.get('/api/user/bookings');
        // Ensure userBookings is always an array
        setUserBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : 
                       bookingsRes.data?.bookings && Array.isArray(bookingsRes.data.bookings) ? bookingsRes.data.bookings : []);
      } catch (bookingErr) {
        // User might not have any bookings yet - ensure it's an empty array
        console.log('No bookings found or user not fully authenticated');
        setUserBookings([]);
      }

    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...businesses];

    // Search term filter
    if (searchTerm) {
      filtered = filtered.filter(business => 
        business.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        business.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (business.description && business.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Type filter
    if (filters.type) {
      filtered = filtered.filter(business => business.type === filters.type);
    }

    // Location filter (basic text search for now)
    if (filters.location) {
      filtered = filtered.filter(business => 
        business.location && business.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Rating filter
    if (filters.rating) {
      const minRating = parseFloat(filters.rating);
      filtered = filtered.filter(business => 
        business.rating && business.rating.averageRating >= minRating
      );
    }

    setFilteredBusinesses(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Get user's current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting user location:', error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    }
  };

  const handleBusinessSelect = async (business) => {
    try {
      // Fetch full business details including photos
      console.log('Fetching business details for ID:', business.id);
      const response = await API.get(`/api/businesses/${business.id}`);
      console.log('Business details response:', response.data);
      console.log('Photos in response:', response.data.business?.photos);
      setSelectedBusiness(response.data.business);
      setShowBusinessDetailModal(true);
    } catch (error) {
      console.error('Error fetching business details:', error);
      // Fallback to basic business data if fetch fails
      setSelectedBusiness(business);
      setShowBusinessDetailModal(true);
    }
  };

  const handleBookNow = (business) => {
    setSelectedBusiness(business);
    setShowBookingModal(true);
  };

  const handleBookingSuccess = (newBooking) => {
    // Ensure userBookings is an array before spreading
    setUserBookings(prev => {
      const currentBookings = Array.isArray(prev) ? prev : [];
      return [...currentBookings, newBooking];
    });
    
    // If the booking is for the user's own business, refresh business reservations
    if (userBusiness && newBooking.business_id === userBusiness.id) {
      fetchBusinessReservations();
    }
    
    setShowBookingModal(false);
    setSelectedBusiness(null);
    // Success message is already shown by the BookingModal component
    console.log('Booking success handled in parent component');
  };

  const fetchBusinessReservations = async () => {
    if (!userBusiness) return;
    
    try {
      const reservationsRes = await API.get('/api/business/reservations');
      const reservations = reservationsRes.data?.reservations || reservationsRes.data || [];
      setBusinessReservations(Array.isArray(reservations) ? reservations : []);
    } catch (error) {
      console.error('Error fetching business reservations:', error);
    }
  };

  const handleBusinessUpdate = async (updatedBusiness) => {
    try {
      // If passed business data directly (from business save), update state
      if (updatedBusiness && typeof updatedBusiness === 'object') {
        setUserBusiness(updatedBusiness);
        console.log('Business updated in parent state:', updatedBusiness);
        return;
      }
      
      // Otherwise, make API call to update business (legacy behavior)
      const response = await API.put('/api/business/update', updatedBusiness);
      setUserBusiness(response.data.business);
      setShowBusinessEditModal(false);
      alert('Business updated successfully!');
    } catch (err) {
      console.error('Error updating business:', err);
      alert('Failed to update business');
    }
  };

  // Messaging handlers
  const handleSelectConversation = (conversation) => {
    setActiveConversation(conversation);
    markAsRead(conversation.id);
  };

  const handleSendMessage = async (conversationId, content) => {
    try {
      await sendMessage(conversationId, content);
    } catch (err) {
      toast.error('Failed to send message');
      throw err;
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    try {
      await deleteConversation(conversationId);
      toast.success('Conversation deleted');
    } catch (err) {
      toast.error('Failed to delete conversation');
    }
  };

  const handleCreateConversation = async (businessId) => {
    try {
      const conversation = await createConversation(businessId);
      setActiveConversation(conversation);
      setActiveSection('messages');
      toast.success('Conversation started!');
    } catch (err) {
      toast.error('Failed to start conversation');
    }
  };

  // Booking management handlers
  const handleShowBookingDetails = (booking) => {
    console.log('Opening booking details for:', booking);
    console.log('Booking ID:', booking?.id);
    setSelectedBooking(booking);
    setShowBookingDetailsModal(true);
  };

  const handleShowReschedule = (booking) => {
    setSelectedBooking(booking);
    setShowBookingDetailsModal(false); // Close details modal
    setShowRescheduleModal(true); // Open reschedule modal
  };

  const handleShowCancel = (booking) => {
    setSelectedBooking(booking);
    setShowCancelModal(true);
  };

  const handleBookingUpdated = (updatedBooking) => {
    setUserBookings(prev => {
      const currentBookings = Array.isArray(prev) ? prev : [];
      return currentBookings.map(booking => 
        booking.id === updatedBooking.id ? updatedBooking : booking
      );
    });
    
    // Close modals
    setShowRescheduleModal(false);
    setShowCancelModal(false);
    setShowBookingDetailsModal(false);
    setSelectedBooking(null);
  };

  const closeBookingModals = () => {
    setShowBookingDetailsModal(false);
    setShowRescheduleModal(false);
    setShowCancelModal(false);
    setSelectedBooking(null);
  };

  const handleReservationStatusUpdate = async (reservationId, newStatus, reservationDetails = {}) => {
    // Add confirmation for decline actions
    if (newStatus === 'cancelled') {
      const customerName = reservationDetails.customer_name || 'this customer';
      const confirmDecline = window.confirm(
        `Are you sure you want to decline the reservation for ${customerName}?\n\nThis action will notify the customer that their reservation has been declined.`
      );
      
      if (!confirmDecline) {
        return; // User cancelled the action
      }
    }

    try {
      let response;
      let actionMessage = '';

      // Use specific endpoints for accept/decline for better tracking
      if (newStatus === 'confirmed') {
        response = await acceptReservation(reservationId);
        actionMessage = `✅ Reservation confirmed for ${reservationDetails.customer_name || 'customer'}!`;
      } else if (newStatus === 'cancelled') {
        response = await declineReservation(reservationId);
        actionMessage = `❌ Reservation declined for ${reservationDetails.customer_name || 'customer'}!`;
      } else {
        // Fallback to generic status update
        response = await API.put(`/api/business/reservations/${reservationId}`, { status: newStatus });
        actionMessage = `⏳ Reservation status updated to ${newStatus}!`;
      }
      
      // Update the local state with the returned data
      setBusinessReservations(prev => {
        const currentReservations = Array.isArray(prev) ? prev : [];
        return currentReservations.map(res => 
          res.id === reservationId 
            ? { ...res, status: newStatus, updated_at: new Date().toISOString() } 
            : res
        );
      });
      
      // Show success notification
      showToastNotification(actionMessage, 'success');
      
      // Log the action for debugging
      console.log(`Reservation ${reservationId} ${newStatus} successfully:`, {
        reservationId,
        newStatus,
        customer: reservationDetails.customer_name,
        timestamp: new Date().toISOString()
      });
      
    } catch (err) {
      console.error('Error updating reservation:', err);
      
      // Show detailed error message
      const errorMessage = err.response?.data?.error || 'Failed to update reservation status. Please try again.';
      showToastNotification(errorMessage, 'error');
    }
  };

  // Helper function to show toast notifications
  const showToastNotification = (message, type = 'success') => {
    const toast = document.createElement('div');
    const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';
    toast.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300 transform translate-x-full max-w-sm`;
    toast.innerHTML = `
      <div class="flex items-center space-x-2">
        <span>${message}</span>
        <button onclick="this.parentElement.parentElement.remove()" class="ml-2 text-white hover:text-gray-200">
          ✕
        </button>
      </div>
    `;
    document.body.appendChild(toast);
    
    // Animate in
    setTimeout(() => {
      toast.style.transform = 'translateX(0)';
    }, 100);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      if (document.body.contains(toast)) {
        toast.style.transform = 'translateX(100%)';
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 300);
      }
    }, 5000);
  };

  const handleLogout = async () => {
    try {
      await API.post('/auth/logout');
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
      navigate('/login');
    }
  };

  // Pagination logic
  const indexOfLastBusiness = currentPage * businessesPerPage;
  const indexOfFirstBusiness = indexOfLastBusiness - businessesPerPage;
  const currentBusinesses = filteredBusinesses.slice(indexOfFirstBusiness, indexOfLastBusiness);
  const totalPages = Math.ceil(filteredBusinesses.length / businessesPerPage);

  const businessTypes = [
    { value: '', label: 'All Types' },
    { value: 'barbershop', label: 'Barbershop' },
    { value: 'beauty_salon', label: 'Beauty Salon' },
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'cafe', label: 'Café' },
    { value: 'football_field', label: 'Football Field' },
    { value: 'tennis_court', label: 'Tennis Court' },
    { value: 'gym', label: 'Gym' },
    { value: 'car_wash', label: 'Car Wash' },
    { value: 'spa', label: 'Spa & Wellness' },
    { value: 'dentist', label: 'Dentist' },
    { value: 'doctor', label: 'Doctor' },
    { value: 'other', label: 'Other' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto dark:border-blue-400"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <p className="text-red-600 text-lg dark:text-red-400">{error}</p>
          <button
            onClick={fetchInitialData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <AnimatedLayout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-all duration-300">
        {/* Modern Sidebar */}
        <ModernSidebar
          user={user}
          userBusiness={userBusiness}
          userBookings={userBookings}
          businessReservations={businessReservations}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          onLogout={handleLogout}
        />

        {/* Main Content */}
        <div className="lg:ml-[280px] ml-0 transition-all duration-300">
          <main className="min-h-screen p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
            <div className="max-w-7xl mx-auto w-full">
              
              <AnimatePresence mode="wait">
                {activeSection === 'discover' && (
                  <DiscoverSection 
                    key="discover"
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    filters={filters}
                    setFilters={setFilters}
                    businessTypes={businessTypes}
                    filteredBusinesses={filteredBusinesses}
                    currentBusinesses={currentBusinesses}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    handleBusinessSelect={handleBusinessSelect}
                    handleBookNow={handleBookNow}
                    navigate={navigate}
                    loading={loading}
                    setShowMapModal={setShowMapModal}
                    getCurrentLocation={getCurrentLocation}
                  />
                )}

                {activeSection === 'bookings' && (
                  <BookingsSection 
                    key="bookings"
                    userBookings={userBookings}
                    setActiveSection={setActiveSection}
                    onShowBookingDetails={handleShowBookingDetails}
                    onShowReschedule={handleShowReschedule}
                    onShowCancel={handleShowCancel}
                  />
                )}

                {activeSection === 'messages' && (
                  <MessagesSection
                    key="messages"
                    conversations={conversations}
                    activeConversation={activeConversation}
                    messages={messages}
                    currentUserId={user?.id}
                    onSelectConversation={handleSelectConversation}
                    onSendMessage={handleSendMessage}
                    onDeleteConversation={handleDeleteConversation}
                    loading={messagesLoading}
                  />
                )}

                {activeSection === 'business' && userBusiness && (
                  <BusinessSection 
                    key="business"
                    userBusiness={userBusiness}
                    businessReservations={businessReservations}
                    handleReservationStatusUpdate={handleReservationStatusUpdate}
                    onBusinessUpdate={handleBusinessUpdate}
                    businessRating={businessRating}
                  />
                )}
                
                {activeSection === 'business-calendar' && userBusiness && (
                  <BusinessCalendarPage
                    key="business-calendar"
                  />
                )}

                {activeSection === 'profile' && (
                  <ProfileSection 
                    key="profile"
                    user={user}
                    userBusiness={userBusiness}
                    setActiveSection={setActiveSection}
                  />
                )}
              </AnimatePresence>
            </div>
          </main>
        </div>

        {/* Modals */}
        {showBusinessDetailModal && selectedBusiness && (
          <BusinessDetailModal
            business={selectedBusiness}
            onClose={() => {
              setShowBusinessDetailModal(false);
              setSelectedBusiness(null);
            }}
            onBookNow={(business) => {
              setShowBusinessDetailModal(false);
              setShowBookingModal(true);
            }}
            onContactBusiness={handleCreateConversation}
          />
        )}

        {showBookingModal && selectedBusiness && (
          <BookingModal
            business={selectedBusiness}
            isOpen={showBookingModal}
            onClose={() => {
              setShowBookingModal(false);
              setSelectedBusiness(null);
            }}
            onBookingSuccess={handleBookingSuccess}
          />
        )}

        {showBusinessEditModal && (
          <BusinessEditModal
            business={userBusiness}
            isOpen={showBusinessEditModal}
            onClose={() => setShowBusinessEditModal(false)}
            onSave={handleBusinessUpdate}
          />
        )}

        {/* Booking Management Modals */}
        <SimpleBookingDetailsModal
          isOpen={showBookingDetailsModal}
          onClose={closeBookingModals}
          booking={selectedBooking}
          onReschedule={handleShowReschedule}
        />

        <RescheduleBookingModal
          isOpen={showRescheduleModal}
          onClose={closeBookingModals}
          booking={selectedBooking}
          onBookingUpdated={handleBookingUpdated}
        />

        <CancelBookingModal
          isOpen={showCancelModal}
          onClose={closeBookingModals}
          booking={selectedBooking}
          onBookingUpdated={handleBookingUpdated}
        />

        <BusinessMapModal
          isOpen={showMapModal}
          onClose={() => setShowMapModal(false)}
          businesses={filteredBusinesses}
          onBusinessSelect={handleBusinessSelect}
          userLocation={userLocation}
        />
      </div>
    </AnimatedLayout>
  );
}

// Discover Section Component
const DiscoverSection = ({ 
  searchTerm, setSearchTerm, filters, setFilters, businessTypes, 
  filteredBusinesses, currentBusinesses, totalPages, currentPage, setCurrentPage,
  handleBusinessSelect, handleBookNow, navigate, loading,
  setShowMapModal, getCurrentLocation
}) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    transition={{ duration: 0.3 }}
    className="space-y-8"
  >
    {/* Header */}
    <motion.div 
      className="text-center mb-8"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
    >
      <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
        Discover Amazing Businesses
      </h1>
      <p className="text-gray-600 dark:text-gray-300 text-base sm:text-lg max-w-2xl mx-auto px-4 sm:px-0">
        Find and book the perfect service for your needs. From barbershops to restaurants, we've got you covered.
      </p>
    </motion.div>

    {/* Search and Filters */}
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <Card className="shadow-xl border-2">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="🔍 Search businesses, services, or locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-12 sm:h-14 text-base sm:text-lg px-4 sm:px-6"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                className="h-12 sm:h-14 px-4 sm:px-6 border border-input rounded-md bg-background focus:ring-2 focus:ring-ring min-w-[180px] text-sm sm:text-base"
              >
                {businessTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              <Input
                type="text"
                placeholder="📍 Location..."
                value={filters.location}
                onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                className="h-12 sm:h-14 min-w-[200px] text-sm sm:text-base px-4 sm:px-6"
              />
            </div>
          </div>
      
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <motion.p 
              className="text-muted-foreground text-sm sm:text-base"
              key={filteredBusinesses.length}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <span className="font-semibold text-primary">{filteredBusinesses.length}</span> businesses found
            </motion.p>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setShowMapModal(true);
                  getCurrentLocation(); // Get user location when opening map
                }}
                variant="outline"
                className="gap-2 text-sm sm:text-base"
              >
                <span>🗺️</span>
                Map View
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>

    {/* Business Grid */}
    <motion.div 
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8"
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.1,
            delayChildren: 0.3
          }
        }
      }}
    >
      {currentBusinesses.map((business) => (
        <motion.div
          key={business.id}
          variants={{
            hidden: { opacity: 0, y: 30, scale: 0.9 },
            visible: { 
              opacity: 1, 
              y: 0, 
              scale: 1,
              transition: { 
                type: "spring", 
                stiffness: 300, 
                damping: 30 
              }
            }
          }}
        >
          <BusinessCard
            business={business}
            onSelect={handleBusinessSelect}
            onBookNow={handleBookNow}
          />
        </motion.div>
      ))}
    </motion.div>

    {/* Pagination */}
    {totalPages > 1 && (
      <motion.div 
        className="flex justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <nav className="flex space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
          >
            ← Previous
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                currentPage === i + 1
                  ? 'bg-blue-600 text-white shadow-lg dark:bg-blue-500'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
          >
            Next →
          </button>
        </nav>
      </motion.div>
    )}

    {filteredBusinesses.length === 0 && !loading && (
      <motion.div 
        className="text-center py-16"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="text-6xl mb-4">🔍</div>
        <p className="text-gray-500 text-xl dark:text-gray-400 mb-6">No businesses found matching your criteria.</p>
        <button
          onClick={() => {
            setSearchTerm('');
            setFilters({ type: '', location: '', rating: '', priceRange: '' });
          }}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          Clear All Filters
        </button>
      </motion.div>
    )}
  </motion.div>
);

// Bookings Section Component
const BookingsSection = ({ 
  userBookings, 
  setActiveSection, 
  onShowBookingDetails, 
  onShowReschedule, 
  onShowCancel 
}) => {
  const [sortBy, setSortBy] = useState('date'); // 'date', 'status', 'business'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'confirmed', 'pending', 'completed', 'cancelled'
  const [searchTerm, setSearchTerm] = useState('');

  // Filter and sort bookings
  const processedBookings = useMemo(() => {
    if (!Array.isArray(userBookings)) return [];
    
    let filtered = userBookings.filter(booking => {
      const matchesSearch = booking.business_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || booking.status === filterStatus;
      return matchesSearch && matchesStatus;
    });

    // Sort bookings
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.booking_date) - new Date(a.booking_date);
        case 'status':
          const statusOrder = { 'confirmed': 0, 'pending': 1, 'completed': 2, 'cancelled': 3 };
          return statusOrder[a.status] - statusOrder[b.status];
        case 'business':
          return a.business_name.localeCompare(b.business_name);
        default:
          return 0;
      }
    });

    return filtered;
  }, [userBookings, sortBy, filterStatus, searchTerm]);

  const getStatusStats = () => {
    if (!Array.isArray(userBookings)) return {};
    
    return userBookings.reduce((acc, booking) => {
      acc[booking.status] = (acc[booking.status] || 0) + 1;
      acc.total = (acc.total || 0) + 1;
      return acc;
    }, {});
  };

  const stats = getStatusStats();

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-center mb-6"
      >
        <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
          My Bookings
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Manage your reservations and appointments
        </p>
      </motion.div>

      {!Array.isArray(userBookings) || userBookings.length === 0 ? (
        <motion.div 
          className="text-center py-20 bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-blue-900/20 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          <motion.div 
            className="text-8xl mb-8"
            animate={{ 
              rotate: [0, 10, -10, 0],
              scale: [1, 1.1, 1, 1.05, 1]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut"
            }}
          >
            📅
          </motion.div>
          <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">
            No bookings yet
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-8 text-lg max-w-md mx-auto">
            Start by discovering amazing businesses near you and make your first reservation!
          </p>
          <motion.button
            onClick={() => setActiveSection('discover')}
            className="px-8 py-4 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white rounded-2xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 font-medium text-lg"
            whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="mr-2">🔍</span>
            Discover Businesses
          </motion.button>
        </motion.div>
      ) : (
        <>
          {/* Stats Cards - More Compact */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { staggerChildren: 0.05 }
              }
            }}
          >
            <motion.div 
              variants={{ hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1 } }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-3 border border-blue-200 dark:border-blue-700/50"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{stats.total || 0}</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 font-medium">Total</p>
                </div>
                <span className="text-lg">📊</span>
              </div>
            </motion.div>

            <motion.div 
              variants={{ hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1 } }}
              className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-3 border border-green-200 dark:border-green-700/50"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xl font-bold text-green-600 dark:text-green-400">{stats.confirmed || 0}</p>
                  <p className="text-xs text-green-700 dark:text-green-300 font-medium">Confirmed</p>
                </div>
                <span className="text-lg">✅</span>
              </div>
            </motion.div>

            <motion.div 
              variants={{ hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1 } }}
              className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 rounded-xl p-3 border border-yellow-200 dark:border-yellow-700/50"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{stats.pending || 0}</p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 font-medium">Pending</p>
                </div>
                <span className="text-lg">⏳</span>
              </div>
            </motion.div>

            <motion.div 
              variants={{ hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1 } }}
              className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-3 border border-purple-200 dark:border-purple-700/50"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xl font-bold text-purple-600 dark:text-purple-400">{stats.completed || 0}</p>
                  <p className="text-xs text-purple-700 dark:text-purple-300 font-medium">Completed</p>
                </div>
                <span className="text-lg">🎉</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Filters and Search - More Compact */}
          <motion.div 
            className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md border border-gray-200 dark:border-gray-700"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="🔍 Search by business name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 transition-all duration-200"
                />
              </div>
              
              <div className="flex gap-2">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white min-w-[120px] text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white min-w-[120px] text-sm"
                >
                  <option value="date">Sort by Date</option>
                  <option value="status">Sort by Status</option>
                  <option value="business">Sort by Business</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Bookings List - Grid Layout for Compact Display */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.05, delayChildren: 0.4 }
              }
            }}
          >
            <AnimatePresence>
              {processedBookings.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="col-span-full text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-xl"
                >
                  <span className="text-4xl mb-2 block">🔍</span>
                  <p className="text-gray-500 dark:text-gray-400">
                    No bookings match your search criteria
                  </p>
                </motion.div>
              ) : (
                processedBookings.map((booking, index) => (
                  <motion.div
                    key={booking.id}
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      visible: { 
                        opacity: 1, 
                        y: 0,
                        transition: { delay: index * 0.05 }
                      }
                    }}
                    layout
                  >
                    <BookingCard 
                      booking={booking} 
                      onShowDetails={onShowBookingDetails}
                      onShowReschedule={onShowReschedule}
                      onShowCancel={onShowCancel}
                    />
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </motion.div>
        </>
      )}
    </motion.div>
  );
};

// Business Section Component with detailed business information
const BusinessSection = ({ 
  userBusiness, 
  businessReservations = [], 
  handleReservationStatusUpdate,
  onBusinessUpdate,
  businessRating 
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    type: '',
    phone: '',
    location: '',
    description: '',
    latitude: '',
    longitude: '',
    instagram_url: '',
    facebook_url: '',
    business_hours: null
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Initialize form data when userBusiness changes
  useEffect(() => {
    if (userBusiness) {
      console.log('useEffect: Initializing editFormData with userBusiness:', userBusiness);
      console.log('useEffect: userBusiness.business_hours:', userBusiness.business_hours);
      
      setEditFormData({
        name: userBusiness.name || '',
        type: userBusiness.type || '',
        phone: userBusiness.phone || '',
        location: userBusiness.location || '',
        description: userBusiness.description || '',
        latitude: userBusiness.latitude || '',
        longitude: userBusiness.longitude || '',
        instagram_url: userBusiness.instagram_url || '',
        facebook_url: userBusiness.facebook_url || '',
        business_hours: userBusiness.business_hours || null
      });
      
      console.log('useEffect: Set business_hours to:', userBusiness.business_hours || null);
    }
  }, [userBusiness]);

  const businessTypes = [
    { value: 'barbershop', label: 'Barbershop' },
    { value: 'beauty_salon', label: 'Beauty Salon' },
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'cafe', label: 'Café' },
    { value: 'football_field', label: 'Football Field' },
    { value: 'tennis_court', label: 'Tennis Court' },
    { value: 'gym', label: 'Gym' },
    { value: 'car_wash', label: 'Car Wash' },
    { value: 'spa', label: 'Spa & Wellness' },
    { value: 'dentist', label: 'Dentist' },
    { value: 'doctor', label: 'Doctor' },
    { value: 'other', label: 'Other' }
  ];

  const handleEditToggle = () => {
    setIsEditingDetails(!isEditingDetails);
    setMessage('');
    if (!isEditingDetails) {
      // Reset form data when entering edit mode
      console.log('handleEditToggle: Entering edit mode with userBusiness:', userBusiness);
      console.log('handleEditToggle: Current userBusiness.business_hours:', userBusiness?.business_hours);
      
      setEditFormData({
        name: userBusiness?.name || '',
        type: userBusiness?.type || '',
        phone: userBusiness?.phone || '',
        location: userBusiness?.location || '',
        description: userBusiness?.description || '',
        latitude: userBusiness?.latitude || '',
        longitude: userBusiness?.longitude || '',
        instagram_url: userBusiness?.instagram_url || '',
        facebook_url: userBusiness?.facebook_url || '',
        business_hours: userBusiness?.business_hours || null
      });
      
      console.log('handleEditToggle: Set editFormData.business_hours to:', userBusiness?.business_hours || null);
    }
  };

  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBusinessHoursChange = useCallback((businessHours) => {
    console.log('Business hours changed:', businessHours);
    setEditFormData(prev => ({ ...prev, business_hours: businessHours }));
  }, []);

  const handleLocationSelect = (locationData) => {
    console.log('Location selected:', locationData);
    setEditFormData(prev => ({
      ...prev,
      latitude: locationData.lat,
      longitude: locationData.lng,
      location: locationData.address || prev.location // Update location with the address from geocoding
    }));
  };

  const handleSave = async () => {
    if (!userBusiness) {
      setMessage('No business found to update. Please register your business first.');
      return;
    }

    // Basic validation
    if (!editFormData.name?.trim()) {
      setMessage('Business name is required.');
      return;
    }

    if (!editFormData.type) {
      setMessage('Business type is required.');
      return;
    }

    if (!editFormData.location?.trim()) {
      setMessage('Location is required.');
      return;
    }

    setLoading(true);
    setMessage('');
    
    console.log('handleSave: Attempting to save business data:', editFormData);
    console.log('handleSave: Current userBusiness:', userBusiness);
    
    try {
      const response = await API.put('/api/business/update', editFormData);
      console.log('handleSave: Success response:', response);
      setMessage('Business details updated successfully!');
      setIsEditingDetails(false);
      
      // Update the parent component's userBusiness state with the new data
      if (response.data.business) {
        // Call the parent's update function if available
        if (typeof onBusinessUpdate === 'function') {
          onBusinessUpdate(response.data.business);
        }
        
        // Also trigger a page refresh after a short delay to ensure all components are updated
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        // Fallback: just refresh the page
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error('handleSave: Error occurred:', error);
      console.error('handleSave: Error response:', error.response);
      
      let errorMessage = 'Failed to update business details';
      
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (error.response.status === 404) {
          errorMessage = 'Business not found. Please register your business first.';
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Cannot connect to server. Please check if the server is running.';
      } else {
        errorMessage = error.message;
      }
      
      console.error('handleSave: Final error message:', errorMessage);
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditFormData({
      name: userBusiness?.name || '',
      type: userBusiness?.type || '',
      phone: userBusiness?.phone || '',
      location: userBusiness?.location || '',
      description: userBusiness?.description || '',
      latitude: userBusiness?.latitude || '',
      longitude: userBusiness?.longitude || '',
      instagram_url: userBusiness?.instagram_url || '',
      facebook_url: userBusiness?.facebook_url || '',
      business_hours: userBusiness?.business_hours || null
    });
    setIsEditingDetails(false);
    setMessage('');
  };

  // Calculate analytics
  const analytics = React.useMemo(() => {
    // Ensure businessReservations is always an array
    const reservations = Array.isArray(businessReservations) ? businessReservations : [];
    
    const now = new Date();
    const today = new Date().toDateString();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();

    const todayReservations = reservations.filter(r => 
      r.date && new Date(r.date).toDateString() === today
    ).length;

    const monthlyReservations = reservations.filter(r => {
      if (!r.date) return false;
      const reservationDate = new Date(r.date);
      return reservationDate.getMonth() === thisMonth && reservationDate.getFullYear() === thisYear;
    }).length;

    const totalRevenue = reservations
      .filter(r => r.status === 'confirmed')
      .reduce((sum, r) => sum + (r.price || 0), 0);

    const pendingReservations = reservations.filter(r => r.status === 'pending').length;

    return {
      todayReservations,
      monthlyReservations,
      totalReservations: reservations.length,
      pendingReservations,
      totalRevenue,
      averageRating: businessRating !== null ? businessRating.toFixed(1) : '0.0'
    };
  }, [businessReservations, businessRating]);

  const navigationItems = [
    { id: 'overview', label: 'Overview', icon: '📊', description: 'Dashboard & Analytics' },
    { 
      id: 'reservations', 
      label: 'Reservations', 
      icon: '📅', 
      description: `Manage Bookings${analytics.pendingReservations > 0 ? ` (${analytics.pendingReservations} pending)` : ''}`,
      badge: analytics.pendingReservations || null
    },
    { id: 'details', label: 'Business Details', icon: '🏢', description: 'Edit Information' },
  ];

  // Ensure we have a safe array for rendering
  const safeReservations = Array.isArray(businessReservations) ? businessReservations : [];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          My Business Management
        </h2>
      </div>

      {/* Business Header Card */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="shadow-2xl border-2">
          <CardHeader className="pb-4">
            <div className="flex items-center space-x-4">
              <Avatar className="w-16 h-16 ring-4 ring-primary/20">
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-2xl font-bold">
                  {userBusiness?.name?.charAt(0) || 'B'}
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-xl">
                  {userBusiness?.name || 'My Business'}
                </CardTitle>
                <CardDescription className="capitalize text-base">
                  {userBusiness?.type?.replace('_', ' ') || 'Business'} • {userBusiness?.location || 'No location set'}
                </CardDescription>
                <div className="flex items-center mt-2 space-x-4">
                  <Badge variant="outline" className="gap-1">
                    <span>📞</span> {userBusiness?.phone || 'No phone'}
                  </Badge>
                  <Badge variant="outline" className="gap-1">
                    <span>✉️</span> {userBusiness?.email || 'No email'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Pending Reservations - Most Important */}
          <Card className={`shadow-md hover:shadow-xl transition-shadow ${
            analytics.pendingReservations > 0 
              ? 'bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/50 dark:to-orange-800/50 ring-2 ring-orange-200 dark:ring-orange-700' 
              : 'bg-gradient-to-r from-muted/50 to-muted'
          }`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${
                    analytics.pendingReservations > 0 
                      ? 'text-orange-600 dark:text-orange-300' 
                      : 'text-muted-foreground'
                  }`}>
                    Pending
                  </p>
                  <p className={`text-2xl font-bold ${
                    analytics.pendingReservations > 0 
                      ? 'text-orange-700 dark:text-orange-200' 
                      : 'text-foreground'
                  }`}>
                    {analytics.pendingReservations}
                  </p>
                  {analytics.pendingReservations > 0 && (
                    <Badge variant="secondary" className="text-xs mt-1 bg-orange-500 text-white">
                      Needs action!
                    </Badge>
                  )}
                </div>
                <div className={`text-3xl ${
                  analytics.pendingReservations > 0 
                    ? 'text-orange-500' 
                    : 'text-muted-foreground'
                }`}>
                  ⏳
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/50 dark:to-blue-800/50 shadow-md hover:shadow-xl transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 dark:text-blue-300 text-sm font-medium">Today</p>
                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-200">{analytics.todayReservations}</p>
                </div>
                <div className="text-blue-500 text-3xl">📅</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/50 dark:to-green-800/50 shadow-md hover:shadow-xl transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 dark:text-green-300 text-sm font-medium">This Month</p>
                  <p className="text-2xl font-bold text-green-700 dark:text-green-200">{analytics.monthlyReservations}</p>
                </div>
                <div className="text-green-500 text-3xl">📊</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/50 dark:to-purple-800/50 shadow-md hover:shadow-xl transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-600 dark:text-purple-300 text-sm font-medium">Revenue</p>
                  <p className="text-2xl font-bold text-purple-700 dark:text-purple-200">${analytics.totalRevenue}</p>
                </div>
                <div className="text-purple-500 text-3xl">💰</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-r from-yellow-50 to-yellow-100 dark:from-yellow-900/50 dark:to-yellow-800/50 shadow-md hover:shadow-xl transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-600 dark:text-yellow-300 text-sm font-medium">Rating</p>
                  <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-200">{analytics.averageRating}⭐</p>
                </div>
                <div className="text-yellow-500 text-3xl">⭐</div>
              </div>
            </CardContent>
          </Card>
        </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Navigation Tabs */}
      <Card className="shadow-lg border-2">
        <CardContent className="p-2">
          <div className="flex flex-col sm:flex-row gap-2">
            {navigationItems.map((item) => (
              <Button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                variant={activeTab === item.id ? 'default' : 'ghost'}
                className={`flex-1 relative gap-2 ${
                  activeTab === item.id 
                    ? 'shadow-md' 
                    : ''
                }`}
              >
                <span className="text-lg">{item.icon}</span>
                <span className="font-medium">{item.label}</span>
                {item.badge > 0 && (
                  <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {item.badge}
                  </Badge>
                )}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Recent Reservations */}
            <Card className="shadow-xl border-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Recent Reservations</CardTitle>
                  <Badge variant="secondary">
                    {safeReservations.filter(r => r.status === 'pending').length} pending
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {safeReservations.slice(0, 5).map((reservation, index) => (
                    <Card key={reservation.id || index} className="bg-muted/50">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold">
                                {reservation.customer_name?.charAt(0) || 'U'}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-semibold">{reservation.customer_name || 'Unknown Customer'}</p>
                              <p className="text-sm text-muted-foreground">
                                {reservation.date ? new Date(reservation.date).toLocaleDateString() : 'No date'} at {reservation.time || 'No time'}
                              </p>
                              {reservation.notes && (
                                <p className="text-xs text-muted-foreground mt-1 max-w-xs truncate">
                                  {reservation.notes}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <Badge variant={
                              reservation.status === 'confirmed' ? 'default' :
                              reservation.status === 'pending' ? 'secondary' :
                              'destructive'
                            }>
                              {reservation.status}
                            </Badge>
                            {reservation.status === 'pending' && (
                              <div className="flex space-x-2">
                                <Button
                                  onClick={() => handleReservationStatusUpdate(reservation.id, 'confirmed', reservation)}
                                  size="sm"
                                  className="bg-green-500 hover:bg-green-600 h-8"
                                  title="Confirm Reservation"
                                >
                                  ✓ Confirm
                                </Button>
                                <Button
                                  onClick={() => handleReservationStatusUpdate(reservation.id, 'cancelled', reservation)}
                                  size="sm"
                                  variant="destructive"
                                  className="h-8"
                                  title="Decline Reservation"
                                >
                                  ✗ Decline
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {safeReservations.length === 0 && (
                    <div className="text-center py-8">
                      <div className="text-gray-400 text-4xl mb-3">📅</div>
                      <p className="text-muted-foreground">No reservations yet</p>
                      <p className="text-sm text-muted-foreground">Your reservations will appear here</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {activeTab === 'reservations' && (
          <motion.div
            key="reservations"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Filter Bar */}
            <Card className="shadow-xl border-2">
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <CardTitle className="text-xl">All Reservations</CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="gap-1">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      Pending: {safeReservations.filter(r => r.status === 'pending').length}
                    </Badge>
                    <Badge variant="secondary" className="gap-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      Confirmed: {safeReservations.filter(r => r.status === 'confirmed').length}
                    </Badge>
                    <Badge variant="secondary" className="gap-1">
                      <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                      Cancelled: {safeReservations.filter(r => r.status === 'cancelled').length}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Reservations List */}
            <div className="space-y-4">
              {safeReservations.map((reservation, index) => (
                <motion.div 
                  key={reservation.id || index} 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="shadow-xl border-2 hover:shadow-2xl transition-shadow duration-300">
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
                        <div className="flex items-center space-x-4">
                          <Avatar className="w-12 h-12 ring-2 ring-primary/20">
                            <AvatarFallback className="bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold text-lg">
                              {reservation.customer_name?.charAt(0) || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h5 className="text-lg font-semibold">{reservation.customer_name || 'Unknown Customer'}</h5>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                📅 {reservation.date ? new Date(reservation.date).toLocaleDateString() : 'No date'}
                              </span>
                              <span className="flex items-center gap-1">
                                🕐 {reservation.time || 'No time'}
                              </span>
                              {reservation.phone && (
                                <span className="flex items-center gap-1">
                                  📞 {reservation.phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Badge variant={
                          reservation.status === 'confirmed' ? 'default' :
                          reservation.status === 'pending' ? 'secondary' :
                          'destructive'
                        } className="text-sm px-4 py-2">
                          {reservation.status ? (reservation.status.charAt(0).toUpperCase() + reservation.status.slice(1)) : 'Unknown'}
                        </Badge>
                      </div>
                      
                      {reservation.notes && (
                        <Card className="bg-muted/50 mb-4">
                          <CardContent className="p-3">
                            <p className="text-sm text-muted-foreground">
                              <span className="font-medium">Notes:</span> {reservation.notes}
                            </p>
                          </CardContent>
                        </Card>
                      )}

                      {reservation.services && (
                        <div className="mb-4">
                          <p className="text-sm font-medium mb-2">Services:</p>
                          <div className="flex flex-wrap gap-2">
                            {reservation.services.split(',').map((service, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {service.trim()}
                              </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between pt-4 border-t gap-3">
                      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        {reservation.price && (
                          <Badge variant="outline" className="gap-1">
                            <span>💰</span> ${reservation.price}
                          </Badge>
                        )}
                        <span className="text-xs">
                          📍 Created {new Date(reservation.created_at || Date.now()).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {reservation.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => handleReservationStatusUpdate(reservation.id, 'confirmed', reservation)}
                              className="bg-green-500 hover:bg-green-600 gap-2"
                              size="sm"
                            >
                              <span>✓</span>
                              <span>Confirm</span>
                            </Button>
                            <Button
                              onClick={() => handleReservationStatusUpdate(reservation.id, 'cancelled', reservation)}
                              variant="destructive"
                              className="gap-2"
                              size="sm"
                            >
                              <span>✗</span>
                              <span>Decline</span>
                            </Button>
                          </>
                        )}
                        
                        {reservation.status === 'confirmed' && (
                          <Button
                            onClick={() => handleReservationStatusUpdate(reservation.id, 'cancelled', reservation)}
                            variant="secondary"
                            className="gap-2"
                            size="sm"
                          >
                            <span>↩</span>
                            <span>Cancel</span>
                          </Button>
                        )}

                        {reservation.customer_phone && (
                          <Button
                            onClick={() => window.open(`tel:${reservation.customer_phone}`)}
                            variant="outline"
                            className="gap-2"
                            size="sm"
                          >
                            <span>📞</span>
                            <span>Call</span>
                          </Button>
                        )}
                      </div>
                    </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
              
              {safeReservations.length === 0 && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <Card className="shadow-xl border-2">
                    <CardContent className="p-12 text-center">
                      <div className="text-6xl mb-4 opacity-50">📅</div>
                      <CardTitle className="text-xl mb-2">No reservations yet</CardTitle>
                      <p className="text-muted-foreground">
                        When customers make reservations, they'll appear here for you to manage.
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </div>
            
            {/* Business Hours Overview */}
            <ErrorBoundary>
              <WeeklyAvailability 
                businessHours={userBusiness?.business_hours}
                isEditing={false}
              />
            </ErrorBoundary>
          </motion.div>
        )}

        {activeTab === 'details' && (
          <motion.div
            key="details"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="shadow-xl border-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Business Details</CardTitle>
                  {!isEditingDetails ? (
                    <div className="flex gap-3">
                      <Button 
                        onClick={handleEditToggle}
                        className="gap-2"
                      >
                        <span>✏️</span> Edit Business Details
                      </Button>
                      <Button 
                        onClick={handleEditToggle}
                        variant="secondary"
                        className="gap-2 bg-green-500 hover:bg-green-600 text-white"
                      >
                        <span>🕒</span> Manage Hours
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleSave}
                        disabled={loading}
                        className="gap-2 bg-green-500 hover:bg-green-600"
                      >
                        {loading ? '⏳' : '💾'} {loading ? 'Saving...' : 'Save'}
                      </Button>
                      <Button 
                        onClick={handleCancel}
                        variant="outline"
                      >
                        <span>❌</span> Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>

            {/* Message */}
            {message && (
              <div className={`mb-4 p-3 rounded-lg ${
                message.includes('success') 
                  ? 'bg-green-100 text-green-700 border border-green-200' 
                  : 'bg-red-100 text-red-700 border border-red-200'
              }`}>
                {message}
              </div>
            )}

            {!isEditingDetails ? (
              /* View Mode */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Business Name</label>
                  <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {userBusiness?.name || 'Not set'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Business Type</label>
                  <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg capitalize">
                    {userBusiness?.type?.replace('_', ' ') || 'Not set'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone</label>
                  <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {userBusiness?.phone || 'Not set'}
                  </p>
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
                  <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {userBusiness?.location || 'Not set'}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                  <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg min-h-[100px]">
                    {userBusiness?.description || 'No description available'}
                  </p>
                </div>
                
                {/* Social Media URLs Display */}
                {(userBusiness?.instagram_url || userBusiness?.facebook_url) && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Social Media</label>
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg space-y-2">
                      {userBusiness?.instagram_url && (
                        <div className="flex items-center">
                          <span className="mr-2">📸</span>
                          <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Instagram:</span>
                          <a 
                            href={userBusiness.instagram_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-pink-600 dark:text-pink-400 hover:underline text-sm break-all"
                          >
                            {userBusiness.instagram_url}
                          </a>
                        </div>
                      )}
                      {userBusiness?.facebook_url && (
                        <div className="flex items-center">
                          <span className="mr-2">👥</span>
                          <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Facebook:</span>
                          <a 
                            href={userBusiness.facebook_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:underline text-sm break-all"
                          >
                            {userBusiness.facebook_url}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Business Photos Display */}
                {userBusiness?.photos && userBusiness.photos.length > 0 && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Business Photos ({userBusiness.photos.length}/10)
                    </label>
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {userBusiness.photos.map((photo, index) => (
                          <div key={photo.id} className="relative group">
                            <img
                              src={photo.photo_url}
                              alt={`Business photo ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg shadow-sm group-hover:shadow-md transition-shadow"
                            />
                            {photo.is_primary && (
                              <div className="absolute top-1 left-1 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                                ⭐ Primary
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                        💡 To manage photos, visit the Business Profile section in the main dashboard
                      </p>
                    </div>
                  </div>
                )}
                
                {userBusiness?.latitude && userBusiness?.longitude && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Map Location</label>
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-2">
                        <span className="mr-2">�️</span>
                        <span>Business location has been set on the map</span>
                      </div>
                      <details className="cursor-pointer">
                        <summary className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                          Show coordinates
                        </summary>
                        <div className="mt-2 text-xs font-mono text-gray-500 dark:text-gray-400">
                          Lat: {parseFloat(userBusiness.latitude).toFixed(6)}, Lng: {parseFloat(userBusiness.longitude).toFixed(6)}
                        </div>
                      </details>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Edit Mode */
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      value={editFormData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter business name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Business Type *
                    </label>
                    <select
                      value={editFormData.type}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    >
                      <option value="">Select business type</option>
                      {businessTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={editFormData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      value={editFormData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter business location or select from map below"
                      required
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      💡 Tip: Click on the map below to automatically fill this field with the address
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={editFormData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Describe your business..."
                    />
                  </div>
                  
                  {/* Social Media URLs */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Instagram URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={editFormData.instagram_url}
                      onChange={(e) => handleInputChange('instagram_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="https://instagram.com/yourbusiness"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Facebook URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={editFormData.facebook_url}
                      onChange={(e) => handleInputChange('facebook_url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="https://facebook.com/yourbusiness"
                    />
                  </div>
                </div>

                {/* Map Picker Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Location on Map
                  </label>
                  <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                    <LeafletMapPicker
                      onLocationSelect={handleLocationSelect}
                      initialLocation={
                        editFormData.latitude && editFormData.longitude
                          ? { lat: parseFloat(editFormData.latitude), lng: parseFloat(editFormData.longitude) }
                          : null
                      }
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    🎯 Click anywhere on the map to set your business location. This will automatically update the location field above with the address.
                  </p>
                </div>

                {/* Coordinates Display */}
                {editFormData.latitude && editFormData.longitude && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Map Coordinates (Technical Details)
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Latitude:</span>
                        <div className="font-mono text-sm">{parseFloat(editFormData.latitude).toFixed(6)}</div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Longitude:</span>
                        <div className="font-mono text-sm">{parseFloat(editFormData.longitude).toFixed(6)}</div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      ℹ️ These coordinates are automatically set when you click on the map
                    </p>
                  </div>
                )}
                
                {/* Weekly Business Hours */}
                <ErrorBoundary>
                  <WeeklyAvailability 
                    businessHours={editFormData.business_hours}
                    onHoursChange={handleBusinessHoursChange}
                    isEditing={true}
                  />
                </ErrorBoundary>
              </div>
            )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Profile Section Component
// Messages Section Component
const MessagesSection = ({ 
  conversations, 
  activeConversation, 
  messages, 
  currentUserId,
  onSelectConversation,
  onSendMessage,
  onDeleteConversation,
  loading 
}) => {
  const [showMobileChat, setShowMobileChat] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center">
          <svg className="w-8 h-8 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Messages
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Chat with businesses and customers
        </p>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:grid md:grid-cols-3 gap-6">
        {/* Conversation List */}
        <div className="col-span-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden h-[600px]">
          <ConversationList
            conversations={conversations}
            activeConversation={activeConversation}
            onSelectConversation={(conv) => {
              onSelectConversation(conv);
              setShowMobileChat(true);
            }}
            onDeleteConversation={onDeleteConversation}
            loading={loading}
          />
        </div>

        {/* Chat Window */}
        <div className="col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden h-[600px]">
          <ChatWindow
            conversation={activeConversation}
            messages={messages}
            currentUserId={currentUserId}
            onSendMessage={onSendMessage}
            loading={loading}
          />
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden">
        {!showMobileChat ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden h-[600px]">
            <ConversationList
              conversations={conversations}
              activeConversation={activeConversation}
              onSelectConversation={(conv) => {
                onSelectConversation(conv);
                setShowMobileChat(true);
              }}
              onDeleteConversation={onDeleteConversation}
              loading={loading}
            />
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden h-[600px]">
            <ChatWindow
              conversation={activeConversation}
              messages={messages}
              currentUserId={currentUserId}
              onSendMessage={onSendMessage}
              onClose={() => setShowMobileChat(false)}
              loading={loading}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

const ProfileSection = ({ user, userBusiness, setActiveSection }) => {
  const [userData, setUserData] = useState(user);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleProfileUpdate = async () => {
    setLoading(true);
    try {
      const updatedUserData = await authAPI.getMe();
      setUserData(updatedUserData);
      setError(null);
    } catch (err) {
      setError('Failed to refresh profile data. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
        Profile & Settings
      </h2>
      
      {loading && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
          <button 
            onClick={handleProfileUpdate}
            className="ml-4 text-sm underline hover:no-underline"
          >
            Try Again
          </button>
        </div>
      )}
      
      {userData && (
        <UserProfile 
          user={userData} 
          onProfileUpdate={handleProfileUpdate}
          business={userBusiness}
        />
      )}
    </motion.div>
  );
};

// Enhanced BusinessCard component with modern styling
const BusinessCard = ({ business, onSelect, onBookNow }) => {
  const getBusinessIcon = (type) => {
    const icons = {
      barbershop: '✂️',
      beauty_salon: '💄',
      restaurant: '🍽️',
      cafe: '☕',
      football_field: '⚽',
      tennis_court: '🎾',
      gym: '🏋️',
      car_wash: '🚗',
      spa: '🧘',
      dentist: '🦷',
      doctor: '👩‍⚕️',
      other: '🏢'
    };
    return icons[type] || icons.other;
  };

  const getBusinessTypeColor = (type) => {
    const colors = {
      barbershop: 'from-blue-500 to-blue-600',
      beauty_salon: 'from-pink-500 to-rose-600',
      restaurant: 'from-orange-500 to-red-600',
      cafe: 'from-amber-500 to-orange-600',
      football_field: 'from-green-500 to-emerald-600',
      tennis_court: 'from-teal-500 to-cyan-600',
      gym: 'from-purple-500 to-violet-600',
      car_wash: 'from-cyan-500 to-blue-600',
      spa: 'from-emerald-500 to-teal-600',
      dentist: 'from-sky-500 to-blue-600',
      doctor: 'from-indigo-500 to-blue-600',
      other: 'from-gray-500 to-slate-600'
    };
    return colors[type] || colors.other;
  };

  // Function to check if business is available today
  const getAvailabilityStatus = () => {
    if (!business.business_hours) {
      return { status: 'unknown', text: 'Hours Not Set', color: 'text-gray-500', bgColor: 'bg-gray-100 dark:bg-gray-900/30' };
    }

    const today = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[today.getDay()];
    
    const todayHours = business.business_hours[currentDay];
    
    if (!todayHours || !todayHours.isOpen) {
      return { status: 'closed', text: 'Closed Today', color: 'text-red-500', bgColor: 'bg-red-100 dark:bg-red-900/30' };
    }

    const now = today.getHours() * 60 + today.getMinutes();
    const [openHour, openMinute] = todayHours.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = todayHours.closeTime.split(':').map(Number);
    
    const openTime = openHour * 60 + openMinute;
    const closeTime = closeHour * 60 + closeMinute;
    
    if (now < openTime) {
      return { 
        status: 'opening-later', 
        text: `Opens at ${todayHours.openTime}`, 
        color: 'text-blue-600 dark:text-blue-400', 
        bgColor: 'bg-blue-100 dark:bg-blue-900/30' 
      };
    } else if (now >= openTime && now < closeTime) {
      return { 
        status: 'open', 
        text: `Open until ${todayHours.closeTime}`, 
        color: 'text-green-600 dark:text-green-400', 
        bgColor: 'bg-green-100 dark:bg-green-900/30' 
      };
    } else {
      return { 
        status: 'closed', 
        text: 'Closed Now', 
        color: 'text-red-500', 
        bgColor: 'bg-red-100 dark:bg-red-900/30' 
      };
    }
  };

  const availabilityStatus = getAvailabilityStatus();

  return (
    <motion.div 
      className="h-full"
      whileHover={{ 
        scale: 1.03,
        y: -8,
        transition: { duration: 0.3, ease: "easeOut" }
      }}
      whileTap={{ scale: 0.97 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card className="group relative h-full flex flex-col overflow-hidden shadow-lg hover:shadow-2xl border-2 transition-all duration-500 backdrop-blur-sm">
        {/* Animated background gradient */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500"></div>
      
      {/* Business Photo or Gradient Header */}
      {business.primary_photo ? (
        <div className="relative h-52 overflow-hidden">
          <img
            src={business.primary_photo}
            alt={business.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {/* Enhanced Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent"></div>
          {/* Floating Business Type Badge */}
          <div className="absolute top-4 left-4">
            <Badge className={`bg-gradient-to-r ${getBusinessTypeColor(business.type)} text-white font-semibold shadow-lg backdrop-blur-sm border border-white/20 hover:scale-105 transition-transform`}>
              <span className="mr-1">{getBusinessIcon(business.type)}</span>
              {business.type.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
          {/* Rating Badge */}
          {business.rating && business.rating.averageRating > 0 && (
            <div className="absolute top-4 right-4">
              <Badge variant="secondary" className="shadow-lg backdrop-blur-sm flex items-center">
                <span className="text-yellow-400 mr-1">⭐</span>
                {business.rating.averageRating}/5
              </Badge>
            </div>
          )}
        </div>
      ) : (
        <div className={`relative h-52 bg-gradient-to-br ${getBusinessTypeColor(business.type)} flex items-center justify-center`}>
          <div className="text-white text-6xl opacity-90">{getBusinessIcon(business.type)}</div>
          {/* Floating Business Type Badge */}
          <div className="absolute top-4 left-4">
            <Badge className="bg-white/20 backdrop-blur-sm text-white font-semibold border border-white/30 hover:bg-white/30 transition-colors">
              {business.type.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
          {/* Rating Badge */}
          {business.rating && business.rating.averageRating > 0 && (
            <div className="absolute top-4 right-4">
              <Badge className="bg-white/20 backdrop-blur-sm text-white font-bold border border-white/30 hover:bg-white/30 transition-colors flex items-center">
                <span className="text-yellow-300 mr-1">⭐</span>
                {business.rating.averageRating}/5
              </Badge>
            </div>
          )}
          {/* Decorative Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.2) 0%, transparent 50%),
                               radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)`
            }} />
          </div>
        </div>
      )}
      
      <CardContent className="flex flex-col flex-grow relative">
        {/* Business Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-bold text-foreground group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
              {business.name}
            </h3>
            {!business.primary_photo && (
              <div className={`w-10 h-10 bg-gradient-to-br ${getBusinessTypeColor(business.type)} rounded-xl flex items-center justify-center text-white text-lg shadow-md`}>
                {getBusinessIcon(business.type)}
              </div>
            )}
          </div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 capitalize tracking-wide">
            {business.type.replace('_', ' ')}
          </p>
        </div>
        
        {/* Description */}
        <p className="text-gray-600 text-sm mb-5 line-clamp-2 dark:text-gray-400 flex-grow leading-relaxed">
          {business.description || 'Professional service provider offering quality solutions for your needs.'}
        </p>
        
        {/* Business Info */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <div className="w-6 h-6 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3">
              <span className="text-blue-600 dark:text-blue-400 text-xs">📍</span>
            </div>
            <span className="truncate">{business.location}</span>
          </div>
          
          {business.rating && business.rating.averageRating > 0 && (
            <div className="flex items-center text-sm">
              <div className="w-6 h-6 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mr-3">
                <span className="text-yellow-600 dark:text-yellow-400 text-xs">⭐</span>
              </div>
              <span className="text-gray-600 dark:text-gray-400">
                {business.rating.averageRating}/5 Rating
              </span>
              {business.rating.totalReviews > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-500 ml-2">
                  ({business.rating.totalReviews} reviews)
                </span>
              )}
            </div>
          )}
          
          {/* Dynamic availability indicator */}
          <div className="flex items-center text-sm">
            <div className={`w-6 h-6 rounded-lg ${availabilityStatus.bgColor} flex items-center justify-center mr-3`}>
              <span className={`${availabilityStatus.color} text-xs`}>🕒</span>
            </div>
            <span className={`${availabilityStatus.color} font-medium`}>
              {availabilityStatus.text}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 mt-auto w-full">
          <Button
            variant="outline"
            onClick={() => onSelect(business)}
            className="flex-1 text-xs font-semibold hover:scale-105 transition-transform gap-1.5 px-3 py-2 h-auto whitespace-nowrap"
          >
            <span>👁️</span>
            <span className="hidden sm:inline">View Details</span>
            <span className="sm:hidden">View</span>
          </Button>
          
          <Button
            onClick={() => onBookNow(business)}
            className="flex-1 bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 text-white text-xs font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all gap-1.5 px-3 py-2 h-auto whitespace-nowrap"
          >
            <span>📅</span>
            <span>Book Now</span>
          </Button>
        </div>
      </CardContent>
      
      {/* Subtle shine effect on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 translate-x-[-100%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-out" />
      </div>
    </Card>
    </motion.div>
  );
};

// BookingCard component for displaying user bookings
const BookingCard = ({ booking, onShowDetails, onShowReschedule, onShowCancel }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'confirmed': 
        return { 
          color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-700',
          icon: '✅',
          bgGradient: 'from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10'
        };
      case 'pending': 
        return { 
          color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700',
          icon: '⏳',
          bgGradient: 'from-yellow-50 to-amber-50 dark:from-yellow-900/10 dark:to-amber-900/10'
        };
      case 'cancelled': 
        return { 
          color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700',
          icon: '❌',
          bgGradient: 'from-red-50 to-rose-50 dark:from-red-900/10 dark:to-rose-900/10'
        };
      case 'completed': 
        return { 
          color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-700',
          icon: '🎉',
          bgGradient: 'from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10'
        };
      default: 
        return { 
          color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600',
          icon: '📅',
          bgGradient: 'from-gray-50 to-slate-50 dark:from-gray-800 dark:to-slate-800'
        };
    }
  };

  const getBusinessIcon = (type) => {
    const icons = {
      barbershop: '✂️',
      beauty_salon: '💄',
      restaurant: '🍽️',
      cafe: '☕',
      football_field: '⚽',
      tennis_court: '🎾',
      gym: '🏋️',
      car_wash: '🚗',
      spa: '🧘',
      dentist: '🦷',
      doctor: '👩‍⚕️',
      other: '🏢'
    };
    return icons[type] || icons.other;
  };

  const statusConfig = getStatusConfig(booking.status);
  const businessIcon = getBusinessIcon(booking.business_type);

  return (
    <motion.div 
      className={`relative overflow-hidden bg-gradient-to-br ${statusConfig.bgGradient} rounded-xl shadow-sm hover:shadow-md border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm`}
      whileHover={{ 
        scale: 1.01, 
        y: -1,
        boxShadow: "0 4px 8px -2px rgba(0, 0, 0, 0.1)"
      }}
      whileTap={{ scale: 0.99 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
    >
      {/* Status indicator stripe */}
      <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${
        booking.status === 'confirmed' ? 'from-green-400 to-emerald-500' :
        booking.status === 'pending' ? 'from-yellow-400 to-amber-500' :
        booking.status === 'cancelled' ? 'from-red-400 to-rose-500' :
        booking.status === 'completed' ? 'from-blue-400 to-indigo-500' :
        'from-gray-400 to-slate-500'
      }`} />

      <div className="p-3">
        {/* Compact Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-white dark:bg-gray-800 shadow-sm flex items-center justify-center text-sm flex-shrink-0">
              {businessIcon}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                {booking.business_name}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 capitalize truncate">
                {booking.business_type?.replace('_', ' ')}
              </p>
            </div>
          </div>
          
          <motion.div 
            className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-medium border ${statusConfig.color} flex-shrink-0`}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <span className="text-xs">{statusConfig.icon}</span>
            <span className="capitalize">{booking.status}</span>
          </motion.div>
        </div>
        
        {/* Compact Info Grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-2">
            <div className="flex items-center space-x-1 mb-1">
              <span className="text-blue-500 text-xs">📅</span>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Date</p>
            </div>
            <p className="text-xs font-semibold text-gray-900 dark:text-white">
              {new Date(booking.booking_date).toLocaleDateString('en-US', {
                month: 'short', 
                day: 'numeric'
              })}
            </p>
          </div>
          
          <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-2">
            <div className="flex items-center space-x-1 mb-1">
              <span className="text-purple-500 text-xs">🕒</span>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Time</p>
            </div>
            <p className="text-xs font-semibold text-gray-900 dark:text-white">
              {booking.booking_time}
            </p>
          </div>
        </div>

        {/* Compact Additional Info */}
        <div className="flex justify-between items-center mb-3 text-xs">
          {booking.total_amount && (
            <div className="flex items-center space-x-1">
              <span className="text-green-500">�</span>
              <span className="font-bold text-green-600 dark:text-green-400">
                {booking.total_amount} TND
              </span>
            </div>
          )}
          
          {booking.notes && (
            <div className="flex items-center space-x-1 flex-1 min-w-0 ml-2">
              <span className="text-orange-500">�</span>
              <span className="text-gray-600 dark:text-gray-400 truncate">
                {booking.notes.length > 15 ? booking.notes.substring(0, 15) + '...' : booking.notes}
              </span>
            </div>
          )}
        </div>

        {/* Compact Action Buttons */}
        <div className="flex gap-1">
          {booking.status === 'confirmed' && (
            <>
              <Button 
                onClick={() => onShowReschedule && onShowReschedule(booking)}
                className="flex-1 h-auto py-1.5 text-xs font-medium bg-yellow-500 hover:bg-yellow-600 gap-1"
                size="sm"
              >
                <span>🔄</span>
                <span>Reschedule</span>
              </Button>
              
              <Button 
                onClick={() => onShowCancel && onShowCancel(booking)}
                variant="destructive"
                className="flex-1 h-auto py-1.5 text-xs font-medium gap-1"
                size="sm"
              >
                <span>🚫</span>
                <span>Cancel</span>
              </Button>
            </>
          )}
          
          <Button 
            onClick={() => onShowDetails && onShowDetails(booking)}
            className={`${booking.status === 'confirmed' ? 'w-full mt-1' : 'flex-1'} h-auto py-1.5 text-xs font-medium gap-1`}
            size="sm"
          >
            <span>👁️</span>
            <span>Details</span>
          </Button>
        </div>
      </div>

      {/* Subtle background pattern - smaller */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.05) 0%, transparent 50%)`
        }} />
      </div>
    </motion.div>
  );
};

// BusinessEditModal component (placeholder)
const BusinessEditModal = ({ business, isOpen, onClose, onSave }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Business</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-6">Business editing functionality coming soon...</p>
        <button
          onClick={onClose}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default Home;